import React, { createContext, useState, useContext, useEffect } from 'react';
import api, { verifyAdmin } from '../services/api';

interface AdminUser {
  id: string;
  email: string;
  role: 'admin' | 'superadmin';
  venueId?: string;
}

interface AdminAuthContextProps {
  admin: AdminUser | null;
  loading: boolean;
  error: string | null;
  loginAdmin: (email: string, password: string) => Promise<boolean>;
  logoutAdmin: () => void;
  isSuperAdmin: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextProps>({
  admin: null,
  loading: false,
  error: null,
  loginAdmin: async () => false,
  logoutAdmin: () => {},
  isSuperAdmin: false,
});

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const isSuperAdmin = admin?.role === 'superadmin';
  
  useEffect(() => {
    // Check if admin is already logged in
    const checkAdminAuth = async () => {
      const storedAdmin = localStorage.getItem('adminUser');
      
      if (storedAdmin) {
        try {
          // Parse the stored admin data
          const adminData = JSON.parse(storedAdmin);
          
          // Fix role mapping for stored data
          if (adminData.user.role === 'super_admin') {
            adminData.user.role = 'superadmin';
            console.log('Mapped stored role super_admin to superadmin');
            localStorage.setItem('adminUser', JSON.stringify(adminData));
          }
          
          // Use the enhanced verifyAdmin function with fallbacks
          console.log('Verifying admin token from localStorage');
          try {
            const response = await verifyAdmin(adminData.token);
            
            if (response.valid) {
              console.log('Admin token verified successfully');
              setAdmin(adminData.user);
            } else {
              console.warn('Admin token is invalid');
              localStorage.removeItem('adminUser');
            }
          } catch (verifyErr) {
            console.error('Admin verification failed:', verifyErr);
            localStorage.removeItem('adminUser');
          }
        } catch (parseErr) {
          console.error('Error parsing stored admin data:', parseErr);
          localStorage.removeItem('adminUser');
        }
      }
      
      setLoading(false);
    };
    
    checkAdminAuth();
  }, []);
  
  const loginAdmin = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Attempting admin login with:', { email, passwordLength: password.length });
      
      try {
        const response = await api.post('/admin/login', { email, password });
        console.log('Login API response:', response.data);
        
        const { user, token } = response.data;
        
        // Fix role mapping from server to client
        if (user.role === 'super_admin') {
          user.role = 'superadmin';
          console.log('Mapped server role super_admin to client role superadmin');
        }
        
        // Store admin data and token
        const adminData = { user, token };
        localStorage.setItem('adminUser', JSON.stringify(adminData));
        
        setAdmin(user);
        console.log('Admin login successful, user set in context:', user);
        return true;
      } catch (err: any) {
        console.error('Admin login API error:', err);
        console.error('Error details:', err.response?.data);
        setError(err.response?.data?.message || 'Login failed');
        return false;
      }
    } finally {
      setLoading(false);
    }
  };
  
  const logoutAdmin = () => {
    localStorage.removeItem('adminUser');
    setAdmin(null);
  };
  
  return (
    <AdminAuthContext.Provider
      value={{
        admin,
        loading,
        error,
        loginAdmin,
        logoutAdmin,
        isSuperAdmin,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => useContext(AdminAuthContext);